// src/features/subscription/services/subscriptionService.js
// Subscription service — RevenueCat on native, Firestore fallback on web.

import { doc, onSnapshot } from 'firebase/firestore';
import { db, COLLECTIONS } from '@/shared/utils/core/firebase-config';
import logger from '@/shared/utils/core/logger';
import { isNative } from '@/shared/utils/core/platform';
export { isNative };

// The entitlement identifier configured in RevenueCat dashboard
export const ENTITLEMENT_ID = 'Clarity Journal Pro';

// Free tier: 10 entries before paywall
export const FREE_ENTRY_LIMIT = 10;

// ─── RevenueCat SDK helpers (all dynamic imports — safe on web) ────────────

async function getPurchases() {
  const { Purchases } = await import('@revenuecat/purchases-capacitor');
  return Purchases;
}

async function getRevenueCatUI() {
  const { RevenueCatUI } = await import('@revenuecat/purchases-capacitor-ui');
  return RevenueCatUI;
}

// ─── Initialisation ────────────────────────────────────────────────────────

// Module-level promise so any caller can await configuration completing.
// Prevents "Purchases has not been configured" if UI is invoked before
// the async configure() call resolves.
let _configurePromise = null;

/**
 * Configure RevenueCat SDK and log in the Firebase user.
 * Called once from main.jsx after Firebase Auth is ready.
 * No-op on web.
 */
export async function configureRevenueCat(userId) {
  if (!isNative()) return;

  // If already configuring (initial call with userId=null), chain onto it
  // rather than starting a parallel configure race.
  if (_configurePromise && !userId) return _configurePromise;

  _configurePromise = (async () => {
    try {
      const Purchases = await getPurchases();
      const isIOS =
        typeof window.Capacitor.getPlatform === 'function' &&
        window.Capacitor.getPlatform() === 'ios';

      const apiKey = isIOS
        ? import.meta.env.VITE_REVENUECAT_IOS_KEY
        : import.meta.env.VITE_REVENUECAT_ANDROID_KEY;

      if (!apiKey) {
        logger.warn('[RevenueCat] No API key found — check VITE_REVENUECAT_IOS/ANDROID_KEY');
        return;
      }

      await Purchases.configure({ apiKey });
      logger.debug('[RevenueCat] Configured');

      if (userId) {
        await Purchases.logIn({ appUserID: userId });
        logger.debug('[RevenueCat] Logged in as', userId);
      }
    } catch (err) {
      logger.error('[RevenueCat] configure error:', err);
    }
  })();

  return _configurePromise;
}

/** Await this before calling any RevenueCat UI method on native. */
async function ensureConfigured() {
  if (_configurePromise) await _configurePromise;
}

// ─── Customer info ──────────────────────────────────────────────────────────

/**
 * Get the current customer info from RevenueCat.
 * Returns null on web or on error.
 */
export async function getCustomerInfo() {
  if (!isNative()) return null;
  try {
    await ensureConfigured();
    const Purchases = await getPurchases();
    const result = await Purchases.getCustomerInfo();
    return result.customerInfo;
  } catch (err) {
    logger.error('[RevenueCat] getCustomerInfo error:', err);
    return null;
  }
}

/**
 * Returns true if "Clarity Journal Pro" entitlement is active.
 */
export function hasActiveEntitlement(customerInfo) {
  if (!customerInfo) return false;
  return ENTITLEMENT_ID in (customerInfo.entitlements?.active ?? {});
}

/**
 * Extract subscription state from a customerInfo object.
 */
export function parseCustomerInfo(customerInfo) {
  if (!customerInfo) return { isPremium: false, isTrial: false, trialEndsAt: null };

  const entitlement = customerInfo.entitlements?.active?.[ENTITLEMENT_ID];
  if (!entitlement) return { isPremium: false, isTrial: false, trialEndsAt: null };

  return {
    isPremium: true,
    isTrial: entitlement.periodType === 'TRIAL',
    trialEndsAt: entitlement.expirationDate ? new Date(entitlement.expirationDate) : null,
  };
}

/**
 * Subscribe to real-time customer info updates (native only).
 * Returns a cleanup function to remove the listener.
 */
export function addCustomerInfoUpdateListener(callback) {
  if (!isNative()) return () => {};

  let cleanupCalled = false;
  let listenerHandle = null;

  (async () => {
    try {
      const Purchases = await getPurchases();
      // Check if cleanup already ran while the import was pending (fast unmount race)
      if (cleanupCalled) return;
      listenerHandle = await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
        callback(parseCustomerInfo(customerInfo));
      });
      // If cleanup ran between the await above and here, remove immediately
      if (cleanupCalled && listenerHandle?.remove) {
        listenerHandle.remove().catch(() => {});
      }
    } catch (err) {
      logger.error('[RevenueCat] addCustomerInfoUpdateListener error:', err);
    }
  })();

  return () => {
    if (cleanupCalled) return;
    cleanupCalled = true;
    if (listenerHandle?.remove) {
      listenerHandle.remove().catch(() => {});
    }
  };
}

// ─── Purchases ─────────────────────────────────────────────────────────────

/**
 * Purchase a package. Returns { customerInfo }.
 * Throws on cancellation or error.
 */
export async function purchase(pkg) {
  if (!isNative()) {
    throw new Error('In-app purchases are only available in the native app.');
  }
  try {
    await ensureConfigured();
    const Purchases = await getPurchases();
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    logger.debug('[RevenueCat] Purchase success');
    return result;
  } catch (err) {
    // USER_CANCELLED is not an error — re-throw so callers can distinguish
    logger.error('[RevenueCat] Purchase failed:', err?.code, err?.message);
    throw err;
  }
}

/**
 * Restore previous purchases. Returns { customerInfo }.
 */
export async function restore() {
  if (!isNative()) {
    throw new Error('Restore purchases is only available in the native app.');
  }
  try {
    await ensureConfigured();
    const Purchases = await getPurchases();
    const result = await Purchases.restorePurchases();
    logger.debug('[RevenueCat] Restore success');
    return result;
  } catch (err) {
    logger.error('[RevenueCat] Restore failed:', err);
    throw err;
  }
}

// ─── Offerings ─────────────────────────────────────────────────────────────

/**
 * Fetch available packages from the current offering.
 * Returns [] on web or error.
 * RevenueCat package identifiers: '$rc_monthly', '$rc_annual', '$rc_lifetime'
 */
export async function getPackages() {
  if (!isNative()) return [];
  try {
    await ensureConfigured();
    const Purchases = await getPurchases();
    const { current } = await Purchases.getOfferings();
    return current?.availablePackages ?? [];
  } catch (err) {
    logger.error('[RevenueCat] getPackages error:', err);
    return [];
  }
}

// ─── RevenueCat Paywall UI ──────────────────────────────────────────────────

/**
 * Present the RevenueCat native paywall (native only).
 * Returns after the user dismisses the paywall (subscribe or cancel).
 * Returns { purchaseMade: boolean }.
 */
export async function presentPaywall() {
  if (!isNative()) return { purchaseMade: false };
  try {
    await ensureConfigured();
    const RevenueCatUI = await getRevenueCatUI();
    const result = await RevenueCatUI.presentPaywall();
    logger.debug('[RevenueCat] Paywall dismissed, result:', result);
    return result ?? { purchaseMade: false };
  } catch (err) {
    logger.error('[RevenueCat] presentPaywall error:', err);
    return { purchaseMade: false };
  }
}

/**
 * Present paywall only if the user doesn't have "Clarity Journal Pro".
 * Ideal for entry-point gates — avoids showing paywall to existing subscribers.
 * Returns { purchaseMade: boolean }.
 */
export async function presentPaywallIfNeeded() {
  if (!isNative()) return { purchaseMade: false };
  try {
    await ensureConfigured();
    const RevenueCatUI = await getRevenueCatUI();
    const result = await RevenueCatUI.presentPaywallIfNeeded({
      requiredEntitlementIdentifier: ENTITLEMENT_ID,
    });
    logger.debug('[RevenueCat] presentPaywallIfNeeded result:', result);
    return result ?? { purchaseMade: false };
  } catch (err) {
    logger.error('[RevenueCat] presentPaywallIfNeeded error:', err);
    return { purchaseMade: false };
  }
}

// ─── Customer Center ────────────────────────────────────────────────────────

/**
 * Present the RevenueCat Customer Center (subscription management + support).
 * Lets users: cancel, request refunds, check status, contact support.
 * Native only — no-op on web.
 */
export async function presentCustomerCenter() {
  if (!isNative()) return;
  try {
    await ensureConfigured();
    const RevenueCatUI = await getRevenueCatUI();
    await RevenueCatUI.presentCustomerCenter();
    logger.debug('[RevenueCat] Customer Center dismissed');
  } catch (err) {
    logger.error('[RevenueCat] presentCustomerCenter error:', err);
  }
}

// ─── Firestore listeners (web fallback + webhook sync) ─────────────────────

/**
 * Subscribe to subscription status via Firestore userProfiles/{uid}.subscriptionTier.
 * Used on web (RevenueCat SDK unavailable) and as a secondary confirmation on native
 * (RevenueCat webhook writes to Firestore within seconds of purchase).
 */
export function subscribeToSubscriptionStatus(uid, onUpdate, onError) {
  if (!uid) return () => {};

  const ref = doc(db, COLLECTIONS.USER_PROFILES, uid);
  return onSnapshot(
    ref,
    (snap) => {
      const data = snap.data() || {};
      onUpdate({
        isPremium: data.subscriptionTier === 'premium',
        isTrial: data.isTrial ?? false,
        trialEndsAt: data.subscriptionExpiresAt?.toDate?.() ?? null,
      });
    },
    (err) => {
      logger.error('[SubscriptionService] subscriptionTier listener error:', err);
      if (onError) onError(err);
    }
  );
}

/**
 * Subscribe to entry count from userStats/{uid}.totalEntries.
 * userStats is a lightweight hot-stats doc written alongside userAIMemory.
 * Source of truth for the free-tier gate (not journal entries collection).
 */
export function subscribeToEntryCount(uid, onUpdate, onError) {
  if (!uid) return () => {};

  const ref = doc(db, COLLECTIONS.USER_STATS, uid);
  return onSnapshot(
    ref,
    (snap) => {
      const count = snap.data()?.totalEntries ?? 0;
      onUpdate(count);
    },
    (err) => {
      logger.error('[SubscriptionService] entryCount listener error:', err);
      if (onError) onError(err);
    }
  );
}

export function isOverFreeLimit(entryCount, isPremium) {
  return !isPremium && entryCount >= FREE_ENTRY_LIMIT;
}

// ─── Web purchases (RevenueCat Billing via purchases-js) ────────────────────

let _webPurchases = null;
let _webPurchasesUserId = null; // track userId separately — don't monkey-patch the SDK object
let _webPurchasesInitPromise = null; // in-flight init promise to prevent concurrent config races

async function getWebPurchasesInstance(userId) {
  // Return cached instance if userId matches
  if (_webPurchases && _webPurchasesUserId === userId) return _webPurchases;

  // Prevent concurrent calls from each triggering their own configure() — they share one promise
  if (_webPurchasesInitPromise && _webPurchasesUserId === userId) return _webPurchasesInitPromise;

  _webPurchasesInitPromise = (async () => {
    const { Purchases } = await import('@revenuecat/purchases-js');
    const apiKey = import.meta.env.VITE_REVENUECAT_WEB_KEY;
    if (!apiKey) throw new Error('VITE_REVENUECAT_WEB_KEY is not set');
    const instance = Purchases.configure(apiKey, userId);
    _webPurchases = instance;
    _webPurchasesUserId = userId;
    _webPurchasesInitPromise = null;
    return instance;
  })();

  return _webPurchasesInitPromise;
}

/**
 * Fetch available packages from RevenueCat Web Billing.
 * Returns [] on native or on error.
 */
export async function getWebPackages(userId) {
  if (isNative() || !userId) return [];
  const purchases = await getWebPurchasesInstance(userId);
  const offerings = await purchases.getOfferings();
  logger.debug('[RevenueCat Web] offerings:', offerings);
  return offerings.current?.availablePackages ?? [];
}

/**
 * Fetch customer info from RevenueCat Web Billing.
 * Returns null on native, on error, or when key is not set.
 */
export async function getWebCustomerInfo(userId) {
  if (isNative() || !userId) return null;
  try {
    const purchases = await getWebPurchasesInstance(userId);
    return await purchases.getCustomerInfo();
  } catch (err) {
    logger.error('[RevenueCat Web] getWebCustomerInfo error:', err);
    return null;
  }
}

/**
 * Get the Stripe customer portal URL for managing a web subscription.
 * Returns null on native, on error, or when no management URL is available.
 */
export async function getWebManagementURL(userId) {
  if (isNative() || !userId) return null;
  try {
    const customerInfo = await getWebCustomerInfo(userId);
    return customerInfo?.managementURL ?? null;
  } catch (err) {
    logger.error('[RevenueCat Web] getWebManagementURL error:', err);
    return null;
  }
}

/**
 * Initiate a web purchase via RevenueCat Billing.
 * The SDK renders an inline Stripe checkout overlay on the page — no redirect.
 * Resolves with { customerInfo } on success, throws on cancel/error.
 */
export async function purchaseOnWeb(userId, rcPackage) {
  if (isNative()) throw new Error('Use native purchase on native platform');
  try {
    const purchases = await getWebPurchasesInstance(userId);
    const result = await purchases.purchase({
      rcPackage,
      skipSuccessPage: true, // We handle the success state ourselves
    });
    logger.debug('[RevenueCat Web] Purchase success');
    return result;
  } catch (err) {
    logger.error('[RevenueCat Web] Purchase error:', err);
    throw err;
  }
}
